import { action, makeObservable, observable } from 'mobx';
import ApiBasedStore from './ApiBasedStore';
import { makePersistable } from 'mobx-persist-store';

export default class DeviceStore extends ApiBasedStore {
  deviceApi;
  deviceTypes = [];
  devices = [];
  customModel = false;

  constructor(rootStore, deviceApi) {
    super(rootStore);
    this.deviceApi = deviceApi;

    makeObservable(this, {
      deviceTypes: observable,
      devices: observable,
      customModel: observable,
      getDeviceTypes: action,
      getDevices: action,
      setCustomModel: action,
    });

    makePersistable(this, {
      name: 'DeviceStore',
      properties: ['customModel'],
    });
  }

  getDeviceTypes = async () => {
    try {
      this.startRequest();
      const { rc, msg, deviceTypes } = await this.deviceApi.getDeviceTypes();

      if (rc === 0) {
        this.deviceTypes = deviceTypes;
      } else {
        this.onError(msg);
      }
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  };

  getDevices = async (deviceTypeID) => {
    try {
      this.startRequest();
      const { rc, msg, devices } = await this.deviceApi.getDevices({
        deviceTypeID,
      });

      if (rc === 0) {
        this.devices = devices;
      } else {
        this.onError(msg);
      }
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  };

  getAllDevices = async () => {
    try {
      this.startRequest();
      
      // Load all device types and combine them
      const phoneResult = await this.deviceApi.getDevices({ deviceTypeID: 1 });
      
      const tabletResult = await this.deviceApi.getDevices({ deviceTypeID: 2 });
      
      const laptopResult = await this.deviceApi.getDevices({ deviceTypeID: 3 });
      
      const consoleResult = await this.deviceApi.getDevices({ deviceTypeID: 4 });

      const graphicsCardResult = await this.deviceApi.getDevices({ deviceTypeID: 5 });

      const allDevices = [];
      
      // Add phone devices if successful
      if (phoneResult.rc === 0 && phoneResult.devices) {
        allDevices.push(...phoneResult.devices);
      }
      
      // Add tablet devices if successful
      if (tabletResult.rc === 0 && tabletResult.devices) {
        allDevices.push(...tabletResult.devices);
      }
      
      // Add laptop devices if successful
      if (laptopResult.rc === 0 && laptopResult.devices) {
        allDevices.push(...laptopResult.devices);
      }
      
      // Add console devices if successful
      if (consoleResult.rc === 0 && consoleResult.devices) {
        allDevices.push(...consoleResult.devices);
      }

      // Add graphics card devices if successful
      if (graphicsCardResult.rc === 0 && graphicsCardResult.devices) {
        allDevices.push(...graphicsCardResult.devices);
      }
      
      return allDevices;
    } catch (e) {
      console.error('DeviceStore.getAllDevices: Error:', e);
      this.onError(e);
      return [];
    } finally {
      this.endRequest();
    }
  };

  getDevice = async (data) => {
    try {
      this.startRequest();
      const { rc, msg, device } = await this.deviceApi.getDevice(data);

      if (rc === 0) {
        return device;
      } else {
        // For "Device not found" (rc: 2), don't call onError - let caller handle gracefully
        // For other errors, still call onError
        if (rc !== 2 || msg !== 'Device not found') {
          this.onError(msg);
        }
        // Return error details so caller can handle "Device not found" (rc: 2) gracefully
        return { rc, msg, device: null };
      }
    } catch (e) {
      this.onError(e);
      return { rc: -1, msg: e.message, device: null };
    } finally {
      this.endRequest();
    }
  };

  addDeviceModel = async (data, device) => {
    try {
      this.startRequest();
      const { rc, msg, model } = await this.deviceApi.addDeviceModel(data);

      if (rc === 0) {
        return model;
      } else {
        this.onError(msg);
      }
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  };

  setCustomModel(modelData) {
    this.customModel = modelData;
  }
}
